import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Users, Share2, Copy, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const CollabBar = ({ experimentId, connected, participants, myId, chat, sendChat, onJoin }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [msg, setMsg] = useState("");

  const createRoom = async () => {
    try {
      const { data } = await api.post("/collab/create", { experiment_id: experimentId });
      onJoin(data.room_code);
      setDialogOpen(false);
      toast.success(`Room ${data.room_code} created!`);
    } catch {
      toast.error("Could not create room. Please log in.");
    }
  };

  const doJoin = () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length < 4) { toast.error("Enter a valid code."); return; }
    onJoin(code);
    setDialogOpen(false);
  };

  const copyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success("Room link copied — share it with your friends!");
  };

  return (
    <div className="flex items-center gap-2">
      {connected && (
        <>
          <div className="flex items-center gap-1.5 px-3 h-8 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs font-mono" data-testid="collab-status">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 anim-pulse-glow" />
            Live · {participants.length} in room
          </div>
          <div className="hidden md:flex -space-x-1.5">
            {participants.slice(0, 4).map((p) => (
              <div key={p.id} title={p.name} className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-slate-900 ${p.id === myId ? "bg-cyan-500 text-slate-950" : "bg-fuchsia-500 text-white"}`}>
                {p.name?.[0]?.toUpperCase() || "?"}
              </div>
            ))}
          </div>
          <Button variant="ghost" size="icon" onClick={copyLink} className="text-cyan-300 hover:bg-slate-800" data-testid="collab-copy-link-btn" aria-label="Copy room link">
            <Copy className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setChatOpen((v) => !v)} className="text-cyan-300 hover:bg-slate-800 relative" data-testid="collab-chat-btn" aria-label="Room chat">
            <MessageSquare className="h-4 w-4" />
            {chat.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-fuchsia-400" />}
          </Button>
        </>
      )}

      {!connected && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="ghost" className="text-cyan-300 hover:bg-slate-800" data-testid="collab-open-btn">
              <Share2 className="mr-2 h-4 w-4" /> Collab
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
            <DialogHeader>
              <DialogTitle className="font-outfit text-cyan-200 flex items-center gap-2"><Users className="h-5 w-5" /> Live Collaboration</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 rounded-xl border border-slate-700 bg-slate-800/60">
                <div className="text-sm text-slate-300 mb-2">Start a room and share the URL with classmates. Every slider you move updates for everyone.</div>
                <Button onClick={createRoom} className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold" data-testid="collab-create-btn">
                  <Share2 className="mr-2 h-4 w-4" /> Create room
                </Button>
              </div>
              <div className="text-center text-xs uppercase tracking-widest text-slate-500 font-bold">or join</div>
              <div className="p-4 rounded-xl border border-slate-700 bg-slate-800/60">
                <div className="flex gap-2">
                  <Input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="Room code" className="bg-slate-900 border-slate-700 text-cyan-100 uppercase font-mono" data-testid="collab-join-code-input" />
                  <Button onClick={doJoin} className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-bold" data-testid="collab-join-btn">Join</Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {chatOpen && connected && (
        <div className="fixed bottom-20 right-6 w-80 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden" data-testid="collab-chat-panel">
          <div className="px-4 py-2 border-b border-slate-800 flex items-center justify-between">
            <div className="font-outfit font-bold text-cyan-200 text-sm">Room chat</div>
            <button onClick={() => setChatOpen(false)} className="text-slate-500 hover:text-slate-300 text-xs font-mono">close</button>
          </div>
          <div className="flex-1 max-h-64 overflow-y-auto p-3 space-y-2">
            {chat.length === 0 ? <div className="text-slate-500 text-xs">No messages yet. Say hi 👋</div> :
              chat.map((m, i) => (
                <div key={i} className="text-sm">
                  <span className="text-cyan-300 font-mono text-xs">{m.name}: </span>
                  <span className="text-slate-200">{m.text}</span>
                </div>
              ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); sendChat(msg); setMsg(""); }} className="flex gap-2 p-2 border-t border-slate-800">
            <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type…" className="bg-slate-800 border-slate-700 h-8 text-sm text-slate-100" data-testid="collab-chat-input" />
            <Button type="submit" size="icon" className="h-8 w-8 bg-cyan-500 hover:bg-cyan-400 text-slate-950" data-testid="collab-chat-send"><Send className="h-3.5 w-3.5" /></Button>
          </form>
        </div>
      )}
    </div>
  );
};

export default CollabBar;
